<template>
  <div
    :class="[
      'asc-selection-grid',
      `asc-selection-grid--${gridSize}`,
      gridFilters ? 'asc-selection-grid--hasFilters' : '',
      disabled ? 'asc-selection-grid--disabled' : '',
    ]"
  >
    <asc-ag-grid-filter
      :key="`asc-selection-grid_${gridId}`"
      :class="getGridClasses"
      :grid-options="getGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
      :selections="selected"
      :page-size-selected="pageSizeSelected"
      :page-sizes="pageSizes"
      :page-size="pageSize"
      :show-rows-per-page="showRowsPerPage"
      @onSelectionChanged="handleSelectionChanged"
      @onRowSelected="handleRowSelected"
    ></asc-ag-grid-filter>
    <!-- eslint-disable-next-line vue/require-component-is -->
    <component
      is="style"
      v-if="selectionIcons && hasCheckBoxSelection"
      type="text/css"
      lang="scss"
    >
      <!-- iconCodes -->
      .asc-ag-grid-filter-{{ _uid }}--container{--icon-size:
      {{ `${iconSize}px` }}; --un-selected-icon :'\{{
        selectionIcons.unSelected
      }}'; --selected-icon: '\{{ selectionIcons.selected }}';
      --indeterminate-icon:'\{{ selectionIcons.indeterminate }}'}
    </component>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscDrillDownCellRenderer,
  AscDrillDownReadCellRenderer,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import _ from 'lodash'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscSelectionGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscDrillDownCellRenderer,
    AscDrillDownReadCellRenderer,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    localStoreName: {
      type: String,
      default: undefined,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    pageSizeSelected: {
      type: Number,
      default: 12,
    },
    pageSizes: {
      type: Array,
      default: () => [],
    },
    pageSize: {
      type: Number,
      default: 12,
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    rowSelection: {
      type: String,
      validator: value => {
        return ['multiple', 'single', undefined].indexOf(value) !== -1
      },
      default: undefined,
    },
    selectedRowHighlight: {
      type: Boolean,
      default: true,
    },
    rowHoverHightlight: {
      type: Boolean,
      default: true,
    },
    iconSize: {
      type: Number,
      default: 18,
    },
    showRowsPerPage: {
      type: Boolean,
      default: true,
    },
    selectionIcons: {
      type: Object,
      validator: value => {
        // for icon codes :: https://pictogrammers.github.io/@mdi/font/5.3.45/
        if (value !== null && typeof value === 'object') {
          return (
            Object.prototype.hasOwnProperty.call(value, 'selected') &&
            Object.prototype.hasOwnProperty.call(value, 'unSelected') &&
            typeof value.selected === 'string' &&
            typeof value.unSelected === 'string'
          )
        }
        return false
      },
      default: undefined,
    },
    noDataMessage: {
      type: String,
      default: 'No Data Displayed',
    },
    gridFilters: {
      type: Boolean,
      default: true,
    },
    gridSize: {
      type: [String, Number],
      default: 'auto',
      validator: val =>
        typeof val === 'string'
          ? ['auto', 'lg', 'md', 'sm', 'max'].includes(val)
          : typeof val === 'number',
    },
    options: {
      type: Object,
      default: () => {},
    },
    getRowId: {
      type: Function,
      default: undefined,
    },
    domLayout: {
      type: String,
      default: 'autoHeight',
      validator: val => ['autoHeight', 'normal'].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localStorageName: this.localStoreName,
    }
  },

  computed: {
    hasCheckBoxSelection() {
      return this.columnDefs.some(def => def.checkboxSelection === true)
    },
    getGridClasses() {
      const gridClasses = [`asc-ag-grid-filter-${this._uid}--container`]

      if (this.hasCheckBoxSelection) {
        gridClasses.push(
          this.selectionIcons ? 'custom-theme-icons' : 'asc-theme-icons',
        )

        gridClasses.push(
          this.isRowMultiSelect
            ? 'multiCheckboxSelectGrid'
            : 'singleRadioSelectGrid',
        )
      } else {
        if (this.rowSelection) {
          gridClasses.push(
            'clickSelect',
            this.isRowMultiSelect ? 'multiSelectGrid' : 'singleSelectGrid',
          )

          if (this.selectedRowHighlight) gridClasses.push('selectedHighlight')
          if (this.rowHoverHightlight) gridClasses.push('rowHoverHightlight')
        }
      }

      return gridClasses
    },
    isRowMultiSelect() {
      return this.rowSelection === 'multiple'
    },
    mappedValues() {
      return this.values
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)
      return this.mappedValues
    },

    getDefaultGridOptions() {
      return getFilterOptions(this, {
        getRowId: this.getRowId,
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        domLayout: this.domLayout,
        localeText: { noRowsToShow: this.noDataMessage },
        rowSelection: this.rowSelection,
        rowMultiSelectWithClick:
          (!this.hasCheckBoxSelection && this.isRowMultiSelect) || undefined,
        suppressRowClickSelection: this.hasCheckBoxSelection || undefined,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
      })
    },

    getGridOptions() {
      return _.merge({}, this.getDefaultGridOptions, this.options)
    },

    getGridColumnDefs() {
      const newColumnDefs = this.columnDefs.map(colDefObj => {
        if (_.has(colDefObj, 'floatingFilterComponent')) {
          if (
            colDefObj.floatingFilterComponent === 'AscAgGridDateFloatFilter'
          ) {
            _.assign(colDefObj, {
              floatingFilterComponentParams: {
                suppressFilterButton: true,
                items: this.values,
              },
              comparator: this.sortDate,
            })
          }

          if (
            colDefObj.floatingFilterComponent ===
            'AscAgGridAutocompleteFloatFilter'
          ) {
            _.assign(colDefObj, {
              floatingFilterComponentParams: {
                ids: [],
                items: this.getUniqueItems(colDefObj.field),
                suppressFilterButton: true,
              },
            })
          }
        }
        return {
          headerName: this.getColumnTitle(
            this.gridColumns,
            colDefObj.field,
            colDefObj.headerName,
          ),
          ...colDefObj,
          sortable: true,
          suppressMenu: true,
          resizable: false,
          editable: false,
          suppressMovable: true,
        }
      })
      return newColumnDefs
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    handleGridReady(params) {
      this.resizeGridColumns(params)
      if (params && params.type === 'gridReady' && params.api)
        this.$emit('onSelectionGridReady', params.api)
    },
    handleRowSelected(selectedNode) {
      if (this.disabled) return false
      this.$emit('onRowSelected', selectedNode)
    },
    handleSelectionChanged(rows) {
      if (!this.disabled)
        this.$emit(
          'onSelectionChanged',
          rows.map(row => row.id),
        )
    },
  },
}
</script>

<style lang="scss">
.asc-selection-grid {
  margin-bottom: 21px;

  &--disabled {
    cursor: not-allowed;

    .ag-input-field-input {
      pointer-events: none;
    }
  }

  .ag-selection-checkbox {
    position: relative;
    top: -5px;
  }

  .ag-theme-balham {
    overflow: scroll;
  }

  .clickSelect {
    .ag-row.ag-row-hover {
      cursor: pointer;
    }

    &.selectedHighlight {
      .ag-row.ag-row-selected {
        background-color: $accent-100;
      }
    }

    &.rowHoverHightlight {
      .ag-row.ag-row-hover:not(.ag-row-selected) {
        background-color: $accent-0;
      }
    }
  }

  &.asc-selection-grid {
    &--hasFilters {
      .ag-overlay-no-rows-wrapper {
        padding-top: 81px;
      }
    }
  }

  .ag-checkbox-input-wrapper {
    &:focus-within,
    &:active {
      outline: none;
      box-shadow: none;
    }

    .ag-checkbox-input {
      &:hover {
        cursor: pointer;
      }
    }
  }

  .custom-theme-icons {
    .ag-checkbox-input-wrapper {
      background-color: transparent;
      margin-bottom: 0;
      font-size: var(--icon-size);
      line-height: var(--icon-size);
      width: var(--icon-size);
      height: var(--icon-size);

      &::after {
        font-family: Material Design Icons, sans-serif;
        content: var(--un-selected-icon);
        color: $gray-600;
      }

      &.ag-checked::after {
        content: var(--selected-icon);
        color: $accent-600;
      }

      &.ag-indeterminate::after {
        content: var(--indeterminate-icon);
        color: $gray-600;
      }
    }
  }

  .asc-theme-icons {
    .ag-checkbox-input-wrapper {
      font-family: inherit;
      background-color: transparent;
      margin-bottom: 0;
      line-height: 16px;
      width: 16px;
      height: 16px;
      border: 1px solid $gray-600;
      border-radius: 0;

      &::after {
        content: '';
        font-family: Material Design Icons, sans-serif;
        color: $primary-black;
        font-size: 13px;
        width: 16px;
        height: 16px;
        text-align: center;
        position: absolute;
        top: -1px;
        left: -1px;
      }

      &.ag-checked {
        background-color: $accent-400;
        border: 1px solid $accent-400;
      }

      &.ag-indeterminate {
        &::after {
          color: $gray-500 !important;
        }
      }
    }

    &.multiCheckboxSelectGrid {
      .ag-checkbox-input-wrapper {
        &.ag-checked {
          &::after {
            content: '\F0E1E';
          }
        }

        &.ag-indeterminate {
          &::after {
            content: '\F0374';
            color: $gray-500 !important;
          }
        }
      }
    }

    &.singleRadioSelectGrid {
      .ag-checkbox-input-wrapper {
        border-radius: 100%;

        &.ag-checked {
          &::after {
            content: '\F044A';
            font-size: 11px;
          }
        }
      }
    }
  }
}
</style>
